import axios, { AxiosInstance, AxiosError } from 'axios';
import { useAuthStore } from '@stores/authStore';
import { config } from '@/config/environment';
import type { LoginCredentials, AuthResponse, ApiResponse } from '../types/index';

class ApiClient {
  private client: AxiosInstance;
  private baseURL: string;

  constructor() {
    this.baseURL = config.api.baseURL;

    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: config.api.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include auth token
    this.client.interceptors.request.use((config) => {
      const token = useAuthStore.getState().token;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Add response interceptor to handle errors
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          useAuthStore.getState().clearAuth();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Authentication endpoints
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      // Check if backend is available first
      try {
        await this.client.get('/health', { timeout: config.api.timeout });
      } catch {
        // Backend not available, use demo credentials
        console.warn('Backend not available, using demo credentials');
        
        if (credentials.email === 'admin@example.com' && credentials.password === 'password123') {
          return {
            token: 'demo-token-' + Date.now(),
            user: {
              id: 'demo-user',
              email: credentials.email,
              name: 'Demo User',
              role: 'admin',
              adapter: 'EBS',
              createdAt: new Date().toISOString(),
            },
            adapter: {
              name: 'Oracle EBS',
              type: 'EBS',
              port: config.adapters.ebs.port,
              status: 'online',
              version: '1.0.0',
            },
          };
        }
        
        throw new Error('Invalid credentials. Use admin@example.com / password123 for demo');
      }
      
      // If backend is available, make the actual request
      const response = await this.client.post<ApiResponse<AuthResponse>>(
        '/api/auth/login',
        {
          ...credentials,
          adapter: 'EBS',
        }
      );
      return response.data.data!;
    } catch (error: any) {
      // If it's a network error, try demo credentials
      if (error.code === 'ERR_NETWORK' || error.code === 'ERR_CONNECTION_REFUSED' || error.message?.includes('timeout')) {
        console.warn('Backend connection failed, using demo credentials');
        
        if (credentials.email === 'admin@example.com' && credentials.password === 'password123') {
          return {
            token: 'demo-token-' + Date.now(),
            user: {
              id: 'demo-user',
              email: credentials.email,
              name: 'Demo User',
              role: 'admin',
              adapter: 'EBS',
              createdAt: new Date().toISOString(),
            },
            adapter: {
              name: 'Oracle EBS',
              type: 'EBS',
              port: config.adapters.ebs.port,
              status: 'online',
              version: '1.0.0',
            },
          };
        }
        
        throw new Error('Invalid credentials. Use admin@example.com / password123 for demo');
      }
      throw error;
    }
  }

  async logout(): Promise<void> {
    await this.client.post('/api/auth/logout');
    useAuthStore.getState().clearAuth();
  }

  async validateToken(): Promise<boolean> {
    try {
      const response = await this.client.get<ApiResponse<{ valid: boolean }>>(
        '/api/auth/validate'
      );
      return response.data.data?.valid ?? false;
    } catch {
      return false;
    }
  }

  /**
   * Health check endpoints
   */
  async getHealth() {
    const response = await this.client.get('/health');
    return response.data;
  }

  async getReady() {
    const response = await this.client.get('/ready');
    return response.data;
  }

  /**
   * AR Invoice endpoints
   */
  async getARInvoices(page = 1, pageSize = 10) {
    try {
      const response = await this.client.get('/api/ar-invoices', {
        params: { page, pageSize },
        timeout: config.api.timeout,
      });
      return response.data.data;
    } catch (error: any) {
      // Backend not available, return mock data
      if (error.code === 'ERR_NETWORK' || error.code === 'ERR_CONNECTION_REFUSED' || error.message?.includes('timeout')) {
        console.warn('Backend not available, using mock AR invoices');
        return this.getMockARInvoices(page, pageSize);
      }
      throw error;
    }
  }

  async getARInvoice(id: string) {
    try {
      const response = await this.client.get(`/api/ar-invoices/${id}`, {
        timeout: config.api.timeout,
      });
      return response.data.data;
    } catch (error: any) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ERR_CONNECTION_REFUSED' || error.message?.includes('timeout')) {
        console.warn('Backend not available, using mock AR invoice');
        return this.getMockARInvoiceDetail(id);
      }
      throw error;
    }
  }

  async processARInvoice(invoiceId: string) {
    try {
      const response = await this.client.post(
        `/api/ar-invoices/${invoiceId}/process`,
        {},
        { timeout: config.api.timeout }
      );
      return response.data.data;
    } catch (error: any) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ERR_CONNECTION_REFUSED' || error.message?.includes('timeout')) {
        console.warn('Backend not available, simulating AR invoice processing');
        return { invoiceId, status: 'processed', timestamp: new Date().toISOString() };
      }
      throw error;
    }
  }

  async retryARInvoice(invoiceId: string) {
    try {
      const response = await this.client.post(
        `/api/ar-invoices/${invoiceId}/retry`,
        {},
        { timeout: config.api.timeout }
      );
      return response.data.data;
    } catch (error: any) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ERR_CONNECTION_REFUSED' || error.message?.includes('timeout')) {
        console.warn('Backend not available, simulating AR invoice retry');
        return { invoiceId, status: 'retried', timestamp: new Date().toISOString() };
      }
      throw error;
    }
  }

  private getMockARInvoices(page: number, pageSize: number) {
    const mockInvoices = [
      {
        id: 'ar-001',
        invoiceNumber: 'INV-2026-001',
        customerName: 'Acme Corporation',
        amount: 45000,
        taxAmount: 4500,
        totalAmount: 49500,
        status: 'completed',
        complianceStatus: 'compliant',
        invoiceDate: '2026-05-20',
        dueDate: '2026-06-20',
        sourceSystem: 'Oracle EBS',
      },
      {
        id: 'ar-002',
        invoiceNumber: 'INV-2026-002',
        customerName: 'Global Tech Solutions',
        amount: 32500,
        taxAmount: 3250,
        totalAmount: 35750,
        status: 'completed',
        complianceStatus: 'compliant',
        invoiceDate: '2026-05-21',
        dueDate: '2026-06-21',
        sourceSystem: 'Oracle Fusion',
      },
      {
        id: 'ar-003',
        invoiceNumber: 'INV-2026-003',
        customerName: 'Middle East Enterprises',
        amount: 67800,
        taxAmount: 6780,
        totalAmount: 74580,
        status: 'processing',
        complianceStatus: 'pending_review',
        invoiceDate: '2026-05-22',
        dueDate: '2026-06-22',
        sourceSystem: 'NetSuite',
      },
      {
        id: 'ar-004',
        invoiceNumber: 'INV-2026-004',
        customerName: 'Dubai Trading Company',
        amount: 23400,
        taxAmount: 2340,
        totalAmount: 25740,
        status: 'completed',
        complianceStatus: 'compliant',
        invoiceDate: '2026-05-19',
        dueDate: '2026-06-19',
        sourceSystem: 'JD Edwards',
      },
      {
        id: 'ar-005',
        invoiceNumber: 'INV-2026-005',
        customerName: 'Emirates Financial Group',
        amount: 89200,
        taxAmount: 8920,
        totalAmount: 98120,
        status: 'failed',
        complianceStatus: 'non_compliant',
        invoiceDate: '2026-05-18',
        dueDate: '2026-06-18',
        sourceSystem: 'PeopleSoft',
      },
      {
        id: 'ar-006',
        invoiceNumber: 'INV-2026-006',
        customerName: 'International Logistics',
        amount: 54300,
        taxAmount: 5430,
        totalAmount: 59730,
        status: 'completed',
        complianceStatus: 'compliant',
        invoiceDate: '2026-05-17',
        dueDate: '2026-06-17',
        sourceSystem: 'Oracle EBS',
      },
      {
        id: 'ar-007',
        invoiceNumber: 'INV-2026-007',
        customerName: 'Premium Services Ltd',
        amount: 41100,
        taxAmount: 4110,
        totalAmount: 45210,
        status: 'processing',
        complianceStatus: 'compliant',
        invoiceDate: '2026-05-16',
        dueDate: '2026-06-16',
        sourceSystem: 'Oracle Fusion',
      },
      {
        id: 'ar-008',
        invoiceNumber: 'INV-2026-008',
        customerName: 'Gulf Region Partners',
        amount: 76500,
        taxAmount: 7650,
        totalAmount: 84150,
        status: 'completed',
        complianceStatus: 'compliant',
        invoiceDate: '2026-05-15',
        dueDate: '2026-06-15',
        sourceSystem: 'NetSuite',
      },
    ];

    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedInvoices = mockInvoices.slice(startIndex, endIndex);

    return {
      data: paginatedInvoices,
      pagination: {
        page,
        pageSize,
        total: mockInvoices.length,
        totalPages: Math.ceil(mockInvoices.length / pageSize),
      },
    };
  }

  private getMockARInvoiceDetail(id: string) {
    const mockInvoice = {
      id,
      invoiceNumber: 'INV-2026-001',
      customerName: 'Acme Corporation',
      customerId: 'CUST-001',
      amount: 45000,
      taxAmount: 4500,
      totalAmount: 49500,
      status: 'completed',
      complianceStatus: 'compliant',
      invoiceDate: '2026-05-20',
      dueDate: '2026-06-20',
      sourceSystem: 'Oracle EBS',
      lineItems: [
        {
          lineNumber: 1,
          description: 'Professional Services',
          quantity: 100,
          unitPrice: 300,
          amount: 30000,
          taxAmount: 3000,
          totalAmount: 33000,
        },
        {
          lineNumber: 2,
          description: 'Software License',
          quantity: 50,
          unitPrice: 300,
          amount: 15000,
          taxAmount: 1500,
          totalAmount: 16500,
        },
      ],
      notes: 'Invoice for Q2 2026 services',
      createdAt: '2026-05-20T10:30:00Z',
      updatedAt: '2026-05-20T10:30:00Z',
    };
    return mockInvoice;
  }

  /**
   * AP Invoice endpoints
   */
  async getAPInvoices(page = 1, pageSize = 10) {
    try {
      const response = await this.client.get('/api/ap-invoices', {
        params: { page, pageSize },
        timeout: config.api.timeout,
      });
      return response.data.data;
    } catch (error: any) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ERR_CONNECTION_REFUSED' || error.message?.includes('timeout')) {
        console.warn('Backend not available, using mock AP invoices');
        return this.getMockAPInvoices(page, pageSize);
      }
      throw error;
    }
  }

  async getAPInvoice(id: string) {
    try {
      const response = await this.client.get(`/api/ap-invoices/${id}`, {
        timeout: config.api.timeout,
      });
      return response.data.data;
    } catch (error: any) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ERR_CONNECTION_REFUSED' || error.message?.includes('timeout')) {
        console.warn('Backend not available, using mock AP invoice');
        return this.getMockAPInvoiceDetail(id);
      }
      throw error;
    }
  }

  async retryAPInvoice(invoiceId: string) {
    try {
      const response = await this.client.post(
        `/api/ap-invoices/${invoiceId}/retry`,
        {},
        { timeout: config.api.timeout }
      );
      return response.data.data;
    } catch (error: any) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ERR_CONNECTION_REFUSED' || error.message?.includes('timeout')) {
        console.warn('Backend not available, simulating AP invoice retry');
        return { invoiceId, status: 'retried', timestamp: new Date().toISOString() };
      }
      throw error;
    }
  }

  private getMockAPInvoices(page: number, pageSize: number) {
    const mockInvoices = [
      {
        id: 'ap-001',
        invoiceNumber: 'PO-INV-2026-001',
        supplierName: 'Tech Supplies Inc',
        poNumber: 'PO-2026-001',
        amount: 28500,
        taxAmount: 2850,
        totalAmount: 31350,
        status: 'completed',
        poMatchStatus: 'matched',
        complianceStatus: 'compliant',
        invoiceDate: '2026-05-20',
        dueDate: '2026-06-20',
        sourceSystem: 'Oracle EBS',
      },
      {
        id: 'ap-002',
        invoiceNumber: 'PO-INV-2026-002',
        supplierName: 'Global Logistics',
        poNumber: 'PO-2026-002',
        amount: 42000,
        taxAmount: 4200,
        totalAmount: 46200,
        status: 'processing',
        poMatchStatus: 'matched',
        complianceStatus: 'compliant',
        invoiceDate: '2026-05-21',
        dueDate: '2026-06-21',
        sourceSystem: 'Oracle Fusion',
      },
      {
        id: 'ap-003',
        invoiceNumber: 'PO-INV-2026-003',
        supplierName: 'Office Equipment Co',
        poNumber: 'PO-2026-003',
        amount: 15600,
        taxAmount: 1560,
        totalAmount: 17160,
        status: 'completed',
        poMatchStatus: 'unmatched',
        complianceStatus: 'pending_review',
        invoiceDate: '2026-05-22',
        dueDate: '2026-06-22',
        sourceSystem: 'NetSuite',
      },
      {
        id: 'ap-004',
        invoiceNumber: 'PO-INV-2026-004',
        supplierName: 'Software Solutions Ltd',
        poNumber: 'PO-2026-004',
        amount: 55000,
        taxAmount: 5500,
        totalAmount: 60500,
        status: 'completed',
        poMatchStatus: 'matched',
        complianceStatus: 'compliant',
        invoiceDate: '2026-05-19',
        dueDate: '2026-06-19',
        sourceSystem: 'JD Edwards',
      },
      {
        id: 'ap-005',
        invoiceNumber: 'PO-INV-2026-005',
        supplierName: 'Consulting Partners',
        poNumber: 'PO-2026-005',
        amount: 72300,
        taxAmount: 7230,
        totalAmount: 79530,
        status: 'failed',
        poMatchStatus: 'partial_match',
        complianceStatus: 'non_compliant',
        invoiceDate: '2026-05-18',
        dueDate: '2026-06-18',
        sourceSystem: 'PeopleSoft',
      },
      {
        id: 'ap-006',
        invoiceNumber: 'PO-INV-2026-006',
        supplierName: 'Maintenance Services',
        poNumber: 'PO-2026-006',
        amount: 18900,
        taxAmount: 1890,
        totalAmount: 20790,
        status: 'completed',
        poMatchStatus: 'matched',
        complianceStatus: 'compliant',
        invoiceDate: '2026-05-17',
        dueDate: '2026-06-17',
        sourceSystem: 'Oracle EBS',
      },
      {
        id: 'ap-007',
        invoiceNumber: 'PO-INV-2026-007',
        supplierName: 'Utilities Provider',
        poNumber: 'PO-2026-007',
        amount: 8500,
        taxAmount: 850,
        totalAmount: 9350,
        status: 'processing',
        poMatchStatus: 'matched',
        complianceStatus: 'compliant',
        invoiceDate: '2026-05-16',
        dueDate: '2026-06-16',
        sourceSystem: 'Oracle Fusion',
      },
      {
        id: 'ap-008',
        invoiceNumber: 'PO-INV-2026-008',
        supplierName: 'Hardware Distributor',
        poNumber: 'PO-2026-008',
        amount: 34200,
        taxAmount: 3420,
        totalAmount: 37620,
        status: 'completed',
        poMatchStatus: 'matched',
        complianceStatus: 'compliant',
        invoiceDate: '2026-05-15',
        dueDate: '2026-06-15',
        sourceSystem: 'NetSuite',
      },
    ];

    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedInvoices = mockInvoices.slice(startIndex, endIndex);

    return {
      data: paginatedInvoices,
      pagination: {
        page,
        pageSize,
        total: mockInvoices.length,
        totalPages: Math.ceil(mockInvoices.length / pageSize),
      },
    };
  }

  private getMockAPInvoiceDetail(id: string) {
    const mockInvoice = {
      id,
      invoiceNumber: 'PO-INV-2026-001',
      supplierName: 'Tech Supplies Inc',
      supplierId: 'SUPP-001',
      poNumber: 'PO-2026-001',
      amount: 28500,
      taxAmount: 2850,
      totalAmount: 31350,
      status: 'completed',
      poMatchStatus: 'matched',
      complianceStatus: 'compliant',
      invoiceDate: '2026-05-20',
      dueDate: '2026-06-20',
      sourceSystem: 'Oracle EBS',
      lineItems: [
        {
          lineNumber: 1,
          description: 'Computer Equipment',
          quantity: 10,
          unitPrice: 1500,
          amount: 15000,
          taxAmount: 1500,
          totalAmount: 16500,
        },
        {
          lineNumber: 2,
          description: 'Software Licenses',
          quantity: 5,
          unitPrice: 2700,
          amount: 13500,
          taxAmount: 1350,
          totalAmount: 14850,
        },
      ],
      notes: 'Invoice for Q2 2026 supplies',
      createdAt: '2026-05-20T10:30:00Z',
      updatedAt: '2026-05-20T10:30:00Z',
    };
    return mockInvoice;
  }

  /**
   * Log endpoints
   */
  async getLogs(page = 1, pageSize = 50) {
    try {
      const response = await this.client.get('/api/logs', {
        params: { page, pageSize },
        timeout: config.api.timeout,
      });
      return response.data.data;
    } catch (error: any) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ERR_CONNECTION_REFUSED' || error.message?.includes('timeout')) {
        console.warn('Backend not available, using mock logs');
        return this.getMockLogs(page, pageSize);
      }
      throw error;
    }
  }

  async getLogStats() {
    try {
      const response = await this.client.get('/api/logs/stats', {
        timeout: config.api.timeout,
      });
      return response.data.data;
    } catch (error: any) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ERR_CONNECTION_REFUSED' || error.message?.includes('timeout')) {
        console.warn('Backend not available, using mock log stats');
        return this.getMockLogStats();
      }
      throw error;
    }
  }

  async exportLogs(format: 'csv' | 'json' = 'csv') {
    try {
      const response = await this.client.get('/api/logs/export', {
        params: { format },
        responseType: 'blob',
        timeout: config.api.timeout * 2, // Double timeout for exports
      });
      return response.data;
    } catch (error: any) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ERR_CONNECTION_REFUSED' || error.message?.includes('timeout')) {
        console.warn('Backend not available, generating mock export');
        return this.generateMockExport(format);
      }
      throw error;
    }
  }

  private getMockLogs(page: number, pageSize: number) {
    const mockLogs = [
      {
        id: 'log-001',
        timestamp: '2026-05-23T10:30:00Z',
        level: 'INFO',
        message: 'Invoice INV-2026-001 processed successfully',
        invoiceId: 'ar-001',
        sourceSystem: 'Oracle EBS',
        details: { status: 'completed', duration: 245 },
      },
      {
        id: 'log-002',
        timestamp: '2026-05-23T10:29:15Z',
        level: 'INFO',
        message: 'Invoice PO-INV-2026-001 matched to PO-2026-001',
        invoiceId: 'ap-001',
        sourceSystem: 'Oracle EBS',
        details: { matchScore: 0.98, duration: 156 },
      },
      {
        id: 'log-003',
        timestamp: '2026-05-23T10:28:30Z',
        level: 'WARNING',
        message: 'Invoice INV-2026-005 compliance check failed',
        invoiceId: 'ar-005',
        sourceSystem: 'PeopleSoft',
        details: { reason: 'Missing tax ID', action: 'manual_review' },
      },
      {
        id: 'log-004',
        timestamp: '2026-05-23T10:27:45Z',
        level: 'INFO',
        message: 'Batch processing started for 50 invoices',
        invoiceId: null,
        sourceSystem: 'System',
        details: { batchId: 'batch-001', count: 50 },
      },
      {
        id: 'log-005',
        timestamp: '2026-05-23T10:26:20Z',
        level: 'ERROR',
        message: 'Database connection timeout',
        invoiceId: null,
        sourceSystem: 'System',
        details: { error: 'Connection pool exhausted', retryCount: 3 },
      },
      {
        id: 'log-006',
        timestamp: '2026-05-23T10:25:00Z',
        level: 'INFO',
        message: 'Invoice INV-2026-003 processing started',
        invoiceId: 'ar-003',
        sourceSystem: 'NetSuite',
        details: { status: 'processing', duration: 0 },
      },
      {
        id: 'log-007',
        timestamp: '2026-05-23T10:24:15Z',
        level: 'INFO',
        message: 'PO matching completed for 8 invoices',
        invoiceId: null,
        sourceSystem: 'System',
        details: { matched: 7, unmatched: 1, accuracy: 0.875 },
      },
      {
        id: 'log-008',
        timestamp: '2026-05-23T10:23:30Z',
        level: 'WARNING',
        message: 'Invoice PO-INV-2026-005 partial match detected',
        invoiceId: 'ap-005',
        sourceSystem: 'PeopleSoft',
        details: { matchScore: 0.65, action: 'manual_review' },
      },
    ];

    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedLogs = mockLogs.slice(startIndex, endIndex);

    return {
      data: paginatedLogs,
      pagination: {
        page,
        pageSize,
        total: mockLogs.length,
        totalPages: Math.ceil(mockLogs.length / pageSize),
      },
    };
  }

  private getMockLogStats() {
    return {
      totalLogs: 1250,
      logsToday: 156,
      errorCount: 12,
      warningCount: 34,
      infoCount: 1204,
      averageResponseTime: 342,
      successRate: 0.991,
      failureRate: 0.009,
      systemHealth: 'healthy',
      lastUpdate: new Date().toISOString(),
    };
  }

  private generateMockExport(format: 'csv' | 'json') {
    const mockData = [
      { timestamp: '2026-05-23T10:30:00Z', level: 'INFO', message: 'Invoice processed', status: 'success' },
      { timestamp: '2026-05-23T10:29:15Z', level: 'INFO', message: 'Invoice matched', status: 'success' },
      { timestamp: '2026-05-23T10:28:30Z', level: 'WARNING', message: 'Compliance check failed', status: 'warning' },
    ];

    if (format === 'json') {
      return new Blob([JSON.stringify(mockData, null, 2)], { type: 'application/json' });
    } else {
      const csv = 'timestamp,level,message,status\n' +
        mockData.map(d => `"${d.timestamp}","${d.level}","${d.message}","${d.status}"`).join('\n');
      return new Blob([csv], { type: 'text/csv' });
    }
  }

  /**
   * PO Matching endpoints
   */
  async getPurchaseOrders(page = 1, pageSize = 10) {
    try {
      const response = await this.client.get('/api/purchase-orders', {
        params: { page, pageSize },
        timeout: config.api.timeout,
      });
      return response.data.data;
    } catch (error: any) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ERR_CONNECTION_REFUSED' || error.message?.includes('timeout')) {
        console.warn('Backend not available, using mock purchase orders');
        return this.getMockPurchaseOrders(page, pageSize);
      }
      throw error;
    }
  }

  async getPurchaseOrder(poNumber: string) {
    try {
      const response = await this.client.get(`/api/purchase-orders/${poNumber}`, {
        timeout: config.api.timeout,
      });
      return response.data.data;
    } catch (error: any) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ERR_CONNECTION_REFUSED' || error.message?.includes('timeout')) {
        console.warn('Backend not available, using mock purchase order');
        return this.getMockPurchaseOrderDetail(poNumber);
      }
      throw error;
    }
  }

  async matchInvoiceToPO(invoiceId: string, poId: string) {
    try {
      const response = await this.client.post('/api/po-matching/match', {
        invoiceId,
        poId,
      }, { timeout: config.api.timeout });
      return response.data.data;
    } catch (error: any) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ERR_CONNECTION_REFUSED' || error.message?.includes('timeout')) {
        console.warn('Backend not available, simulating PO matching');
        return { invoiceId, poId, matchScore: 0.95, status: 'matched' };
      }
      throw error;
    }
  }

  async getMatchingResults(invoiceId: string) {
    try {
      const response = await this.client.get(
        `/api/po-matching/results/${invoiceId}`,
        { timeout: config.api.timeout }
      );
      return response.data.data;
    } catch (error: any) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ERR_CONNECTION_REFUSED' || error.message?.includes('timeout')) {
        console.warn('Backend not available, using mock matching results');
        return this.getMockMatchingResults(invoiceId);
      }
      throw error;
    }
  }

  async getPOMatchingStatistics() {
    try {
      const response = await this.client.get('/api/po-matching/statistics', {
        timeout: config.api.timeout,
      });
      return response.data.data;
    } catch (error: any) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ERR_CONNECTION_REFUSED' || error.message?.includes('timeout')) {
        console.warn('Backend not available, using mock PO matching statistics');
        return this.getMockPOMatchingStats();
      }
      throw error;
    }
  }

  async getPOMatchingConfig() {
    try {
      const response = await this.client.get('/api/po-matching/config', {
        timeout: config.api.timeout,
      });
      return response.data.data;
    } catch (error: any) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ERR_CONNECTION_REFUSED' || error.message?.includes('timeout')) {
        console.warn('Backend not available, using mock PO matching config');
        return this.getMockPOMatchingConfig();
      }
      throw error;
    }
  }

  async updatePOMatchingConfig(config: any) {
    try {
      const response = await this.client.put('/api/po-matching/config', config, {
        timeout: config.api.timeout,
      });
      return response.data.data;
    } catch (error: any) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ERR_CONNECTION_REFUSED' || error.message?.includes('timeout')) {
        console.warn('Backend not available, simulating config update');
        return { ...config, updated: true, timestamp: new Date().toISOString() };
      }
      throw error;
    }
  }

  async linkInvoiceToPO(invoiceId: string, poId: string, matchScore: number) {
    try {
      const response = await this.client.post(
        `/api/ap-invoices/${invoiceId}/link-po`,
        { poId, matchScore },
        { timeout: config.api.timeout }
      );
      return response.data.data;
    } catch (error: any) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ERR_CONNECTION_REFUSED' || error.message?.includes('timeout')) {
        console.warn('Backend not available, simulating PO link');
        return { invoiceId, poId, matchScore, status: 'linked' };
      }
      throw error;
    }
  }

  async confirmPOLink(linkId: string, notes?: string) {
    try {
      const response = await this.client.post(
        `/api/po-links/${linkId}/confirm`,
        { notes },
        { timeout: config.api.timeout }
      );
      return response.data.data;
    } catch (error: any) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ERR_CONNECTION_REFUSED' || error.message?.includes('timeout')) {
        console.warn('Backend not available, simulating PO link confirmation');
        return { linkId, status: 'confirmed', timestamp: new Date().toISOString() };
      }
      throw error;
    }
  }

  async rejectPOLink(linkId: string, notes?: string) {
    try {
      const response = await this.client.post(
        `/api/po-links/${linkId}/reject`,
        { notes },
        { timeout: config.api.timeout }
      );
      return response.data.data;
    } catch (error: any) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ERR_CONNECTION_REFUSED' || error.message?.includes('timeout')) {
        console.warn('Backend not available, simulating PO link rejection');
        return { linkId, status: 'rejected', timestamp: new Date().toISOString() };
      }
      throw error;
    }
  }

  private getMockPurchaseOrders(page: number, pageSize: number) {
    const mockPOs = [
      {
        id: 'po-001',
        poNumber: 'PO-2026-001',
        vendorName: 'Tech Supplies Inc',
        amount: 28500,
        status: 'open',
        createdDate: '2026-05-01',
        deliveryDate: '2026-05-20',
        lineCount: 2,
      },
      {
        id: 'po-002',
        poNumber: 'PO-2026-002',
        vendorName: 'Global Logistics',
        amount: 42000,
        status: 'open',
        createdDate: '2026-05-02',
        deliveryDate: '2026-05-21',
        lineCount: 3,
      },
      {
        id: 'po-003',
        poNumber: 'PO-2026-003',
        vendorName: 'Office Equipment Co',
        amount: 15600,
        status: 'closed',
        createdDate: '2026-04-15',
        deliveryDate: '2026-05-22',
        lineCount: 1,
      },
      {
        id: 'po-004',
        poNumber: 'PO-2026-004',
        vendorName: 'Software Solutions Ltd',
        amount: 55000,
        status: 'open',
        createdDate: '2026-05-03',
        deliveryDate: '2026-05-19',
        lineCount: 4,
      },
      {
        id: 'po-005',
        poNumber: 'PO-2026-005',
        vendorName: 'Consulting Partners',
        amount: 72300,
        status: 'open',
        createdDate: '2026-05-04',
        deliveryDate: '2026-05-18',
        lineCount: 2,
      },
    ];

    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedPOs = mockPOs.slice(startIndex, endIndex);

    return {
      data: paginatedPOs,
      pagination: {
        page,
        pageSize,
        total: mockPOs.length,
        totalPages: Math.ceil(mockPOs.length / pageSize),
      },
    };
  }

  private getMockPurchaseOrderDetail(poNumber: string) {
    return {
      id: 'po-001',
      poNumber,
      vendorName: 'Tech Supplies Inc',
      vendorId: 'VENDOR-001',
      amount: 28500,
      status: 'open',
      createdDate: '2026-05-01',
      deliveryDate: '2026-05-20',
      lineItems: [
        {
          lineNumber: 1,
          description: 'Computer Equipment',
          quantity: 10,
          unitPrice: 1500,
          amount: 15000,
        },
        {
          lineNumber: 2,
          description: 'Software Licenses',
          quantity: 5,
          unitPrice: 2700,
          amount: 13500,
        },
      ],
      notes: 'Standard PO for Q2 supplies',
    };
  }

  private getMockMatchingResults(invoiceId: string) {
    return {
      invoiceId,
      matchedPOs: [
        {
          poNumber: 'PO-2026-001',
          matchScore: 0.98,
          matchType: 'exact',
          variance: 0.02,
        },
      ],
      unmatchedPOs: [],
      status: 'matched',
      timestamp: new Date().toISOString(),
    };
  }

  private getMockPOMatchingStats() {
    return {
      totalInvoices: 156,
      matchedInvoices: 148,
      unmatchedInvoices: 8,
      matchAccuracy: 0.949,
      averageMatchScore: 0.92,
      processingTime: 342,
      lastUpdate: new Date().toISOString(),
    };
  }

  private getMockPOMatchingConfig() {
    return {
      matchingThreshold: 0.85,
      allowPartialMatches: true,
      autoConfirmThreshold: 0.95,
      varianceThreshold: 0.05,
      enableFuzzyMatching: true,
      matchingFields: ['poNumber', 'amount', 'lineItems'],
    };
  }
}

export const apiClient = new ApiClient();
